// ---------------------------------------------------------------------------
// Friendly model naming — one source of truth for every surface that shows a
// tournament / champion model to the user.
//
// Every public library entry has TWO names:
//   • display name — the strategy-based, plain-language name shown first
//     ("Steady baseline"). Describes what the model DOES for the user.
//   • technical label — the stable algorithm identity ("XGBoost ·
//     conservative"), kept one click away (tooltips, detail dialogs) for
//     technical users, support and audit.
//
// The backend ships ``display_name`` on new tournament entries / winners /
// catalog items (core/src/app/discovery/model_library.py). The map below is
// the fallback for documents persisted BEFORE that field existed, keyed by
// the stable preset_id — so old leaderboards read just as well as new ones.
// ---------------------------------------------------------------------------

const PRESET_DISPLAY_NAMES = {
  xgb_conservative: 'Steady baseline',
  xgb_deep: 'Deep pattern learner',
  xgb_belief: 'Confidence-weighted challenger',
  sklearn_histgb: 'Smooth trend learner',
  xgb_quantile: 'Range forecaster',
  ohlcv_specialist: 'Market bars specialist',
  predictfm_zeroshot: 'Pre-trained forecaster',
  predictfm_adapter_adapt: 'Fine-tuned forecaster',
};

/**
 * The user-facing name for anything entry-shaped (tournament entry, winner,
 * catalog item, overview ``stats.model``): prefer the backend's
 * ``display_name``, fall back to the preset map (old docs), then to the
 * technical label so nothing ever renders blank. User-supplied models keep
 * their own name (their ``display_name``/``label`` is the user's name).
 */
export const modelDisplayName = (entry) => {
  if (!entry) return '';
  return entry.display_name
    || PRESET_DISPLAY_NAMES[entry.preset_id || entry.id]
    || entry.label
    || entry.preset_id
    || '';
};

/**
 * The technical identity ("XGBoost · quantile") when it adds information —
 * ``null`` when it's the same string as the display name (user models,
 * unknown entries), so callers can hide a redundant second line.
 */
export const modelTechnicalName = (entry) => {
  if (!entry) return null;
  const tech = entry.tech_label || entry.label;
  if (!tech) return null;
  return tech === modelDisplayName(entry) ? null : tech;
};

// Data-profile traits ("volatility_clustering") in plain language. Falls back
// to de-underscored text for traits not in the vocabulary yet.
const TRAIT_LABELS = {
  smooth: 'smooth data',
  trending: 'trending',
  seasonal: 'seasonal patterns',
  heavy_tails: 'extreme moves',
  multi_regime: 'changing conditions',
  noisy: 'noisy data',
  volatility_clustering: 'volatility bursts',
  spiky: 'sudden spikes',
  multi_year_history: 'multi-year history',
};

export const humanizeTrait = (trait) => (
  TRAIT_LABELS[trait] || String(trait || '').replace(/_/g, ' ')
);

export const humanizeTraits = (traits) => (traits || []).map(humanizeTrait);
